import Image from "next/image";

import { Check, Minus } from "lucide-react";

import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

export interface Competitor {
  name: string;
  priceTier: string;
}

export interface ComparisonFeature {
  name: string;
  availability: boolean[];
}

export interface ComparisonGroup {
  label: string;
  features: ComparisonFeature[];
}

export interface ComparisonSummary {
  line1: string;
  line2: string;
}

interface ComparisonSectionProps {
  competitors: Competitor[];
  groups: ComparisonGroup[];
  summaries: ComparisonSummary[];
}

// ---------------------------------------------------------------------------
// sub-components
// ---------------------------------------------------------------------------

function AvailabilityIcon({ available }: { available: boolean }) {
  return available ? (
    <Check className="w-5 h-5 text-rebolt-500" strokeWidth={2.5} />
  ) : (
    <Minus className="w-5 h-5 text-clay-300" strokeWidth={2} />
  );
}

// ---------------------------------------------------------------------------
// main
// ---------------------------------------------------------------------------

export default function ComparisonSection({ competitors, groups, summaries }: ComparisonSectionProps) {
  return (
    <section
      className="w-full px-5 py-16 md:py-22"
      style={{
        background: "linear-gradient(180deg, #ffffff 9%, #e1e1d9 55%)",
      }}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-10">
        {/* header */}
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="text-xs font-bold uppercase tracking-[1.15px] text-rebolt-500">Feature comparison</span>
          <h2 className="font-clash text-4xl lg:text-[52px] font-medium leading-[1.1] tracking-[-1px] text-ash-900">
            Replace 5 tools with one platform.
          </h2>
          <p className="text-base leading-normal text-ash-600 max-w-lg">
            Agencies, website builders, and home service CRMs each solve one part of the problem. Rebolt combines
            marketing, operations, and automation in one place.
          </p>
        </div>

        {/* ---------- desktop grid table ---------- */}
        <DesktopTable competitors={competitors} groups={groups} summaries={summaries} />

        {/* ---------- mobile stacked view ---------- */}
        <MobileCards competitors={competitors} groups={groups} summaries={summaries} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// desktop table (div-based grid)
// ---------------------------------------------------------------------------

function DesktopTable({ competitors, groups, summaries }: ComparisonSectionProps) {
  const totalFeatureRows = groups.reduce((sum, g) => sum + g.features.length, 0);
  const bodyHeight = totalFeatureRows * 50;

  return (
    <div className="hidden lg:flex w-full max-w-[1140px] overflow-x-auto">
      {/* vertical labels column — sits outside the white table */}
      <div className="flex flex-col shrink-0 w-10">
        <div className="h-[90px]" />
        {groups.map((group) => (
          <div
            key={group.label}
            className="flex items-center justify-center"
            style={{ height: group.features.length * 50 }}
          >
            <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-ash-400 [writing-mode:vertical-lr] rotate-180 whitespace-nowrap">
              {group.label}
            </span>
          </div>
        ))}
        <div className="h-[75px]" />
      </div>

      {/* main table area — white rounded container with Rebolt column overlaying */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* column headers (above the white body) */}
        <div className="flex h-[90px]">
          <div className="w-[250px] shrink-0" />
          <div className="w-[250px] shrink-0 bg-ash-900 rounded-t-3xl flex flex-col items-center justify-between gap-2 px-3 py-3">
            <Image src="/rebolt-branding/rebolt-half-white.svg" alt="Rebolt" width={123} height={30} />
            <span className="text-sm font-bold text-clay-200">$$</span>
          </div>
          {competitors.map((c) => (
            <div key={c.name} className="w-[200px] shrink-0 flex flex-col items-center justify-between gap-2 px-3 py-3">
              <span className="text-sm font-bold text-ash-900 text-center leading-tight whitespace-pre-line">
                {c.name}
              </span>
              <span className="text-sm font-bold text-clay-500">{c.priceTier}</span>
            </div>
          ))}
        </div>

        {/* feature rows body — single white rounded container */}
        <div className="flex rounded-2xl overflow-hidden" style={{ height: bodyHeight }}>
          {/* feature name column */}
          <div className="w-[250px] shrink-0 bg-white flex flex-col">
            {groups.map((group, gi) => (
              <div key={group.label} className={cn("flex flex-col", gi > 0 && "border-t border-ash-300")}>
                {group.features.map((feature) => (
                  <div key={feature.name} className="flex items-center h-[50px] px-4">
                    <span className="text-[13px] font-medium text-ash-900">{feature.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Rebolt column */}
          <div className="w-[250px] shrink-0 bg-ash-900 flex flex-col">
            {groups.map((group) => (
              <div key={group.label} className="flex flex-col">
                {group.features.map((feature) => (
                  <div key={feature.name} className="flex items-center justify-center h-[50px]">
                    <Check className="w-5 h-5 text-rebolt-500" strokeWidth={2.5} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* competitor columns */}
          {competitors.map((competitor, ci) => (
            <div key={competitor.name} className="w-[200px] shrink-0 bg-white flex flex-col">
              {groups.map((group, gi) => (
                <div key={group.label} className={cn("flex flex-col", gi > 0 && "border-t border-ash-300")}>
                  {group.features.map((feature) => (
                    <div key={feature.name} className="flex items-center justify-center h-[50px]">
                      <AvailabilityIcon available={feature.availability[ci]} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* summary row (below the white body) */}
        <div className="flex h-[75px]">
          <div className="w-[250px] shrink-0" />
          <div className="w-[250px] shrink-0 bg-ash-900 rounded-b-3xl flex items-start justify-center px-3 py-4">
            <p className="text-xs font-semibold text-clay-200 leading-snug text-center">
              Built for service pros
              <br />
              Marketing, CRM, and automation in one price
            </p>
          </div>
          {summaries.map((s, i) => (
            <div key={i} className="w-[200px] shrink-0 flex items-start justify-center px-3 py-4">
              <p className="text-xs font-semibold text-ash-700 leading-snug text-center">
                {s.line1}
                <br />
                {s.line2}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// mobile cards
// ---------------------------------------------------------------------------

function MobileCards({ competitors, groups, summaries }: ComparisonSectionProps) {
  return (
    <div className="lg:hidden w-full flex flex-col gap-4">
      {/* Rebolt highlighted card */}
      <div className="rounded-2xl bg-ash-900 p-5">
        <div className="flex items-center justify-between mb-4">
          <Image src="/rebolt-branding/rebolt-half-white.svg" alt="Rebolt" width={100} height={24} />
          <span className="text-sm font-bold text-clay-200">$$</span>
        </div>
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.label}>
              <span className="text-[10px] font-bold uppercase tracking-[1px] text-ash-500 mb-1 block">
                {group.label}
              </span>
              {group.features.map((feature) => (
                <div
                  key={feature.name}
                  className="flex items-center justify-between py-1.5 border-b border-ash-700 last:border-0"
                >
                  <span className="text-xs text-clay-200">{feature.name}</span>
                  <Check className="w-5 h-5 text-rebolt-500 shrink-0" strokeWidth={2.5} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-ash-700">
          <p className="text-xs font-semibold text-clay-200">Built for service pros</p>
          <p className="text-xs text-clay-400">Marketing, CRM, and automation in one price</p>
        </div>
      </div>

      {/* competitor cards */}
      {competitors.map((competitor, ci) => (
        <div key={competitor.name} className="rounded-2xl border border-ash-200 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-ash-900 whitespace-pre-line">{competitor.name}</h4>
            <span className="text-sm font-bold text-clay-500">{competitor.priceTier}</span>
          </div>
          <div className="flex flex-col gap-3">
            {groups.map((group) => (
              <div key={group.label}>
                <span className="text-[10px] font-bold uppercase tracking-[1px] text-ash-400 mb-1 block">
                  {group.label}
                </span>
                {group.features.map((feature) => (
                  <div
                    key={feature.name}
                    className={cn("flex items-center justify-between py-1.5 border-b border-ash-100 last:border-0")}
                  >
                    <span className="text-xs text-ash-700">{feature.name}</span>
                    <AvailabilityIcon available={feature.availability[ci]} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-ash-200">
            <p className="text-xs font-semibold text-ash-700">{summaries[ci].line1}</p>
            <p className="text-xs text-ash-500">{summaries[ci].line2}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
